/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback } from "react";
import { useStateWithCallbackLazy } from "use-state-with-callback";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslate, useLocale } from "react-admin";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { useForm } from "react-hook-form";
import { Grid, Divider, Typography } from "@material-ui/core";
import { TextFieldInForm, MuiAutosuggest, SelectInForm } from "components/Form";
import ActionMenu from "components/ActionMenu";
import ButtonFooter from "components/ButtonFooter";
import {
  getAstCategoryDetail,
  createAstCategoryDetail,
  updateAstCategoryDetail,
  delAstCategoryDetail,
} from "services/asset";
import { getAccountCodeList, getDepartmentList } from "services/setting";
import Model from "models/astCategory";
import SnackbarUtils from "utils/SnackbarUtils";
import { AssetLifeOptions } from "utils/options";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    margin: 20,
  },
  appBar: {
    position: "relative",
    backgroundColor: theme.palette.primary.main,
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  content: {
    padding: 4,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  const classes = useStyles();
  return (
    <MuiDialogTitle disableTypography {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const DialogItem = (props) => {
  const translate = useTranslate();
  const locale = useLocale();
  const classes = useStyles();
  const { children, id, mode, setMode, open, onClose } = props;

  const [data, setData] = useStateWithCallbackLazy(Model);
  const [lookupList, setLookupList] = React.useState({ accountCodeList: [], departmentList: [] });
  const [isBtnLoading, setBtnLoading] = React.useState(false);
  const methods = useForm({ defaultValues: data });

  const { handleSubmit, reset } = methods;

  const fetchDetailById = useCallback(async () => {
    if (id && id !== 0) {
      const response = await getAstCategoryDetail(id);
      if (response) {
        response.AstLifeType = ["Year", "Month", "Day"][response.AstLifeType] || "Year";
        setData(response);
        reset(response);
      }
    } else {
      setData(Model);
      reset(Model);
    }
    setBtnLoading(false);
  }, [id, reset]);

  useEffect(() => {
    fetchDetailById();
  }, [fetchDetailById]);

  useEffect(() => {
    let mounted = true;
    Promise.all([getAccountCodeList("Asset"), getDepartmentList()]).then(([accounts, departments]) => {
      if (mounted) {
        setLookupList({
          accountCodeList: accounts?.Data || [],
          departmentList: departments?.Data || departments || [],
        });
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const disableFormEnter = (e) => {
    if (e.key === "Enter" && e.target.localName !== "textarea") e.preventDefault();
  };

  const onSubmit = (values) => {
    setBtnLoading(true);
    values.CateCode = values.CateCode?.replace(/ /g, "");
    values.AstLife = values.AstLife === "" ? 0 : Number(values.AstLife);
    values.AstLifeType = { Year: 0, Month: 1, Day: 2 }[values.AstLifeType] ?? 0;
    //Adjust parameter before save
    setData(
      (state) => ({
        ...state,
        ...values,
        UserModified: Model.UserModified,
      }),
      (nextState) => Save(nextState)
    );
  };

  const Save = async (values) => {
    if (mode === "edit") {
      values.Id = id;
      //Update
      const { Code, UserMessage } = await updateAstCategoryDetail(values);
      if (Code === 0) {
        SnackbarUtils.success(UserMessage);
        handleClose(id);
      }
    } else {
      const { Code, InternalMessage, UserMessage } = await createAstCategoryDetail(values);
      if (Code === 0) {
        SnackbarUtils.success(UserMessage);
        handleClose(parseInt(InternalMessage));
      }
    }
    setBtnLoading(false);
  };

  const handleClose = (value) => {
    onClose(value);
  };

  const menuControlProp = [
    {
      name: "Edit",
      fnc: () => {
        setMode("edit");
      },
      disabled: mode !== "view",
    },
    {
      name: "Delete",
      fnc: () => DelOrVoid(id),
      disabled: mode !== "view",
    },
  ];

  const DelOrVoid = async (id) => {
    let msg = translate("ra.question.confirmDel");
    SnackbarUtils.delConfirm(msg, async function () {
      const { Code, UserMessage } = await delAstCategoryDetail(id);
      if (Code === 0) {
        SnackbarUtils.success(UserMessage);
        handleClose(id);
      }
    });
  };

  const CancelFnc = () => {
    if (id === 0) {
      onClose();
      return;
    }
    fetchDetailById();
    setMode("view");
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            onClose(event);
          }
        }}
        scroll={"paper"}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="scroll-dialog-title" onClose={onClose}>
          {props.title}
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          <ActionMenu menuControl={menuControlProp} justifyContent="flex-start" />
          <Divider />
          <form onKeyDown={disableFormEnter}>
            <div className={classes.root}>
              <Grid container spacing={2} justifyContent="flex-start">
                <Grid item xs={6} elevation={2}>
                  <TextFieldInForm
                    label={"*" + translate("ra.field.Code")}
                    name="CateCode"
                    variant="outlined"
                    margin="dense"
                    methods={methods}
                    disabled={mode !== "add"}
                    rule={{
                      required: {
                        value: true,
                        message: "* Required",
                      },
                      maxLength: {
                        value: 10,
                        message: "maximum length is 10",
                      },
                    }}
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} elevation={2}>
                  <TextFieldInForm
                    label={translate("ra.field.Description")}
                    name="Description"
                    variant="outlined"
                    margin="dense"
                    methods={methods}
                    disabled={mode === "view"}
                    rule={{
                      maxLength: {
                        value: 255,
                        message: "maximum length is 255",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextFieldInForm
                    label={translate("ra.field.Asset Life")}
                    name="AstLife"
                    type="number"
                    variant="outlined"
                    margin="dense"
                    methods={methods}
                    disabled={mode === "view"}
                    rule={{ min: { value: 0, message: "must be 0 or greater" } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SelectInForm
                    label={translate("ra.field.Asset Life Type")}
                    name="AstLifeType"
                    options={AssetLifeOptions}
                    methods={methods}
                    disabled
                  />
                </Grid>
                {["Cost", "Accu", "Depre"].map((prefix) => (
                  <React.Fragment key={prefix}>
                    <Grid item xs={6}>
                      <MuiAutosuggest
                        label={`${prefix} ${translate("ra.field.Department")}`}
                        name={`${prefix}DeptCode`}
                        optKey="DeptCode"
                        optDesc="Description"
                        options={lookupList.departmentList}
                        updateOtherField={[{ key: `${prefix}DeptDesc`, optKey: "Description" }]}
                        methods={methods}
                        disabled={mode === "view"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextFieldInForm
                        label={`${prefix} ${translate("ra.field.Department")} ${translate("ra.field.Description")}`}
                        name={`${prefix}DeptDesc`}
                        variant="outlined"
                        margin="dense"
                        methods={methods}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MuiAutosuggest
                        label={`${prefix} ${translate("ra.field.Account Code")}`}
                        name={`${prefix}AccCode`}
                        optKey="AccCode"
                        optDesc={locale === "en-US" ? "Description" : "Description2"}
                        options={lookupList.accountCodeList}
                        updateOtherField={[{ key: `${prefix}AccDesc`, optKey: locale === "en-US" ? "Description" : "Description2" }]}
                        methods={methods}
                        disabled={mode === "view"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextFieldInForm
                        label={`${prefix} ${translate("ra.field.Account Code")} ${translate("ra.field.Description")}`}
                        name={`${prefix}AccDesc`}
                        variant="outlined"
                        margin="dense"
                        methods={methods}
                        disabled
                      />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </div>
            {children}
            <Divider />
          </form>
          <pre>{process.env.NODE_ENV === "development" ? JSON.stringify(data, 0, 2) : ""}</pre>
        </DialogContent>
        {mode !== "view" ? (
          <DialogActions>
            <ButtonFooter noBorder disabled={isBtnLoading} SaveFnc={handleSubmit(onSubmit)} CancelFnc={CancelFnc} />
          </DialogActions>
        ) : (
          ""
        )}
      </Dialog>
    </div>
  );
};

export default DialogItem;
